const GEOMETRY_EPSILON = 1e-9;

// O Leaflet pode emitir um click no mapa imediatamente após o dragend de um
// marcador. Este bloqueio curto impede que soltar um vértice seja interpretado
// também como a criação de um novo ponto do polígono.
export function criarProtecaoCliqueAposArraste({ agora = () => Date.now(), toleranciaMs = 320 } = {}) {
  let arrastando = false;
  let bloquearAte = 0;

  return {
    iniciar() {
      arrastando = true;
    },
    encerrar() {
      arrastando = false;
      bloquearAte = agora() + toleranciaMs;
    },
    deveIgnorarClique() {
      return arrastando || agora() < bloquearAte;
    },
  };
}

function sameCoordinate(a, b, epsilon = GEOMETRY_EPSILON) {
  return Math.abs(a.lng - b.lng) <= epsilon && Math.abs(a.lat - b.lat) <= epsilon;
}

function asLatLng(point) {
  if (Array.isArray(point)) {
    return { lng: Number(point[0]), lat: Number(point[1]) };
  }
  return { lng: Number(point?.lng), lat: Number(point?.lat) };
}

export function extrairAnelLatLng(input) {
  const coordinates = input?.geometry?.coordinates?.[0]
    || input?.coordinates?.[0]
    || (Array.isArray(input) ? input : []);
  const ring = coordinates
    .map(asLatLng)
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));

  if (ring.length > 1 && sameCoordinate(ring[0], ring[ring.length - 1])) {
    return ring.slice(0, -1);
  }
  return ring;
}

function project(map, point) {
  const projected = map.latLngToLayerPoint([point.lat, point.lng]);
  return { x: Number(projected.x), y: Number(projected.y) };
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function closestPointOnSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const squaredLength = (dx * dx) + (dy * dy);
  if (squaredLength === 0) return { ...start, t: 0 };
  const t = Math.max(0, Math.min(1, (((point.x - start.x) * dx) + ((point.y - start.y) * dy)) / squaredLength));
  return { x: start.x + (t * dx), y: start.y + (t * dy), t };
}

function shouldReplaceCandidate(current, next) {
  if (!current) return true;
  if (next.distanciaPx < current.distanciaPx - 0.35) return true;
  if (Math.abs(next.distanciaPx - current.distanciaPx) <= 0.35) {
    if (next.tipo === 'vertice' && current.tipo !== 'vertice') return true;
    if ((next.prioridade || 0) > (current.prioridade || 0)) return true;
  }
  return false;
}

/**
 * Encontra o ponto mais próximo de um vértice ou segmento já cadastrado usando
 * a projeção atual do Leaflet. A tolerância permanece visualmente consistente
 * em qualquer nível de zoom porque é medida em pixels, não em graus.
 */
export function encontrarAderenciaNoMapa(latLng, limites = [], map, toleranciaPx = 16) {
  if (!map || !latLng || !limites.length) return null;
  const cursor = project(map, latLng);
  let best = null;

  limites.forEach(limite => {
    const ring = extrairAnelLatLng(limite.geometria);
    if (ring.length < 2) return;
    const projectedRing = ring.map(point => project(map, point));

    ring.forEach((point, index) => {
      const distanciaPx = distance(cursor, projectedRing[index]);
      if (distanciaPx > toleranciaPx) return;
      const candidate = {
        ...point,
        distanciaPx,
        tipo: 'vertice',
        verticeIndice: index,
        segmentoIndice: index,
        limiteId: limite.id,
        limiteNome: limite.nome,
        limiteTipo: limite.tipo,
        prioridade: limite.prioridade || 0,
      };
      if (shouldReplaceCandidate(best, candidate)) best = candidate;
    });

    projectedRing.forEach((start, index) => {
      const endIndex = (index + 1) % projectedRing.length;
      const closest = closestPointOnSegment(cursor, start, projectedRing[endIndex]);
      const distanciaPx = distance(cursor, closest);
      if (distanciaPx > toleranciaPx) return;
      const snapped = {
        lat: ring[index].lat + ((ring[endIndex].lat - ring[index].lat) * closest.t),
        lng: ring[index].lng + ((ring[endIndex].lng - ring[index].lng) * closest.t),
      };
      const candidate = {
        ...snapped,
        distanciaPx,
        tipo: closest.t <= 0.0001 || closest.t >= 0.9999 ? 'vertice' : 'borda',
        verticeIndice: closest.t <= 0.0001 ? index : closest.t >= 0.9999 ? endIndex : null,
        segmentoIndice: index,
        limiteId: limite.id,
        limiteNome: limite.nome,
        limiteTipo: limite.tipo,
        prioridade: limite.prioridade || 0,
      };
      if (shouldReplaceCandidate(best, candidate)) best = candidate;
    });
  });

  return best;
}

export function pontoComAderencia(candidate, fallback) {
  if (!candidate) return { lat: Number(fallback.lat), lng: Number(fallback.lng) };
  return {
    lat: candidate.lat,
    lng: candidate.lng,
    snapSourceId: candidate.limiteId,
    snapSourceName: candidate.limiteNome,
    snapSourceType: candidate.limiteTipo,
    snapType: candidate.tipo,
    snapSegmentIndex: candidate.segmentoIndice,
  };
}

export function criarPontosIntermediarios(points = []) {
  if (points.length < 2) return [];
  return points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    return {
      lat: (Number(point.lat) + Number(next.lat)) / 2,
      lng: (Number(point.lng) + Number(next.lng)) / 2,
      insertIndex: index + 1,
    };
  });
}

export function inserirVertice(points = [], insertIndex, point) {
  if (!Number.isInteger(insertIndex) || !point || !Number.isFinite(Number(point.lat)) || !Number.isFinite(Number(point.lng))) return points;
  const next = [...points];
  next.splice(Math.max(0, Math.min(insertIndex, next.length)), 0, point);
  return next;
}

export function permiteAdicionarPontoNoMapa(modoDesenho, poligonoExistente) {
  return Boolean(modoDesenho && !poligonoExistente);
}

function geographicPathLength(points) {
  return points.slice(1).reduce((total, point, index) => {
    const previous = points[index];
    const meanLatitude = ((previous.lat + point.lat) / 2) * (Math.PI / 180);
    const dx = (point.lng - previous.lng) * Math.cos(meanLatitude);
    const dy = point.lat - previous.lat;
    return total + Math.hypot(dx, dy);
  }, 0);
}

function dedupePath(points) {
  return points.filter((point, index) => index === 0 || !sameCoordinate(point, points[index - 1]));
}

/**
 * Ao ligar dois pontos aderidos à mesma área, reutiliza também as quinas que
 * existem entre eles. Assim uma borda sinuosa não vira uma linha reta que cria
 * um pequeno vazio ou invade o talhão vizinho.
 */
export function expandirTrechoAderente(previous, next, limites = []) {
  if (!previous?.snapSourceId || previous.snapSourceId !== next?.snapSourceId) return [next];
  if (!Number.isInteger(previous.snapSegmentIndex) || !Number.isInteger(next.snapSegmentIndex)) return [next];
  const limite = limites.find(item => String(item.id) === String(next.snapSourceId));
  const ring = extrairAnelLatLng(limite?.geometria);
  if (ring.length < 3) return [next];

  const startSegment = previous.snapSegmentIndex % ring.length;
  const endSegment = next.snapSegmentIndex % ring.length;
  if (startSegment === endSegment) return [next];

  const forwardVertices = [];
  let forwardIndex = (startSegment + 1) % ring.length;
  let guard = 0;
  while (guard < ring.length) {
    forwardVertices.push(ring[forwardIndex]);
    if (forwardIndex === endSegment) break;
    forwardIndex = (forwardIndex + 1) % ring.length;
    guard += 1;
  }

  const backwardVertices = [];
  let backwardIndex = startSegment;
  const backwardTarget = (endSegment + 1) % ring.length;
  guard = 0;
  while (guard < ring.length) {
    backwardVertices.push(ring[backwardIndex]);
    if (backwardIndex === backwardTarget) break;
    backwardIndex = (backwardIndex - 1 + ring.length) % ring.length;
    guard += 1;
  }

  const forwardPath = dedupePath([previous, ...forwardVertices, next]);
  const backwardPath = dedupePath([previous, ...backwardVertices, next]);
  const chosenPath = geographicPathLength(forwardPath) <= geographicPathLength(backwardPath)
    ? forwardPath
    : backwardPath;

  return chosenPath.slice(1).map((point, index, points) => {
    if (index === points.length - 1) return next;
    return {
      lat: point.lat,
      lng: point.lng,
      snapSourceId: next.snapSourceId,
      snapSourceName: next.snapSourceName,
      snapSourceType: next.snapSourceType,
      snapType: 'vertice',
      snapSegmentIndex: ring.findIndex(candidate => sameCoordinate(candidate, point)),
      insertedBySnap: true,
    };
  });
}

// Normaliza todas as arestas antes da validação final. Quando dois vértices
// consecutivos foram aderidos a trechos diferentes da mesma divisa, inclui
// somente as quinas intermediárias indispensáveis para reproduzir a borda
// existente sem cortar para dentro do talhão vizinho.
export function expandirPoligonoAderente(points = [], limites = []) {
  if (points.length < 2) return points;

  const expanded = [points[0]];
  for (let index = 1; index < points.length; index += 1) {
    expanded.push(...expandirTrechoAderente(points[index - 1], points[index], limites));
  }

  const closingPath = expandirTrechoAderente(points[points.length - 1], points[0], limites);
  if (closingPath.length > 1) expanded.push(...closingPath.slice(0, -1));

  return dedupePath(expanded).filter((point, index, result) => (
    index !== result.length - 1 || !sameCoordinate(point, result[0])
  ));
}

function orientation(a, b, c) {
  const value = ((b.lng - a.lng) * (c.lat - a.lat)) - ((b.lat - a.lat) * (c.lng - a.lng));
  if (Math.abs(value) <= GEOMETRY_EPSILON) return 0;
  return value > 0 ? 1 : -1;
}

function pointOnSegment(point, start, end) {
  if (orientation(start, end, point) !== 0) return false;
  return point.lng >= Math.min(start.lng, end.lng) - GEOMETRY_EPSILON
    && point.lng <= Math.max(start.lng, end.lng) + GEOMETRY_EPSILON
    && point.lat >= Math.min(start.lat, end.lat) - GEOMETRY_EPSILON
    && point.lat <= Math.max(start.lat, end.lat) + GEOMETRY_EPSILON;
}

function segmentIntersectionKind(a, b, c, d) {
  const o1 = orientation(a, b, c);
  const o2 = orientation(a, b, d);
  const o3 = orientation(c, d, a);
  const o4 = orientation(c, d, b);
  if ((o1 * o2) < 0 && (o3 * o4) < 0) return 'proper';

  const touches = [
    o1 === 0 && pointOnSegment(c, a, b),
    o2 === 0 && pointOnSegment(d, a, b),
    o3 === 0 && pointOnSegment(a, c, d),
    o4 === 0 && pointOnSegment(b, c, d),
  ].filter(Boolean).length;
  if (!touches) return null;
  if (o1 === 0 && o2 === 0 && o3 === 0 && o4 === 0 && touches > 1) return 'overlap';
  return 'touch';
}

function ringSegments(ring) {
  return ring.map((start, index) => ({ start, end: ring[(index + 1) % ring.length], index }));
}

function pointInPolygon(point, ring) {
  if (ring.some((start, index) => pointOnSegment(point, start, ring[(index + 1) % ring.length]))) {
    return 'boundary';
  }

  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
    const currentPoint = ring[index];
    const previousPoint = ring[previous];
    const intersects = ((currentPoint.lat > point.lat) !== (previousPoint.lat > point.lat))
      && (point.lng < ((previousPoint.lng - currentPoint.lng) * (point.lat - currentPoint.lat)
        / (previousPoint.lat - currentPoint.lat)) + currentPoint.lng);
    if (intersects) inside = !inside;
  }
  return inside ? 'inside' : 'outside';
}

export function poligonoAutoIntercepta(input) {
  const ring = extrairAnelLatLng(input);
  if (ring.length < 3) return true;
  const segments = ringSegments(ring);
  for (let first = 0; first < segments.length; first += 1) {
    for (let second = first + 1; second < segments.length; second += 1) {
      const adjacent = second === first + 1 || (first === 0 && second === segments.length - 1);
      if (adjacent) continue;
      if (segmentIntersectionKind(
        segments[first].start,
        segments[first].end,
        segments[second].start,
        segments[second].end
      )) return true;
    }
  }
  return false;
}

export function poligonoContidoEm(input, limiteInput) {
  const ring = extrairAnelLatLng(input);
  const boundary = extrairAnelLatLng(limiteInput);
  if (ring.length < 3 || boundary.length < 3) return false;
  if (ring.some(point => pointInPolygon(point, boundary) === 'outside')) return false;

  const hasCrossing = ringSegments(ring).some(segment => ringSegments(boundary).some(boundarySegment => (
    segmentIntersectionKind(segment.start, segment.end, boundarySegment.start, boundarySegment.end) === 'proper'
  )));
  return !hasCrossing;
}

export function poligonosTemSobreposicaoInterna(firstInput, secondInput) {
  const first = extrairAnelLatLng(firstInput);
  const second = extrairAnelLatLng(secondInput);
  if (first.length < 3 || second.length < 3) return false;

  if (ringSegments(first).some(a => ringSegments(second).some(b => (
    segmentIntersectionKind(a.start, a.end, b.start, b.end) === 'proper'
  )))) return true;

  if (first.some(point => pointInPolygon(point, second) === 'inside')) return true;
  if (second.some(point => pointInPolygon(point, first) === 'inside')) return true;

  const firstCoincident = first.every(point => pointInPolygon(point, second) === 'boundary');
  const secondCoincident = second.every(point => pointInPolygon(point, first) === 'boundary');
  return firstCoincident && secondCoincident;
}

export function validarGeometriaTalhao({ geometria, propriedade, talhoesExistentes = [] }) {
  if (poligonoAutoIntercepta(geometria)) {
    return { valido: false, codigo: 'auto_intersecao', mensagem: 'O desenho cruza sobre ele mesmo. Ajuste os pontos antes de concluir.' };
  }
  if (propriedade && !poligonoContidoEm(geometria, propriedade)) {
    return { valido: false, codigo: 'fora_propriedade', mensagem: 'O talhão precisa ficar totalmente dentro do perímetro da propriedade.' };
  }

  const sobreposto = talhoesExistentes.find(item => (
    item?.geometria && poligonosTemSobreposicaoInterna(geometria, item.geometria)
  ));
  if (sobreposto) {
    return {
      valido: false,
      codigo: 'sobreposicao',
      item: sobreposto,
      mensagem: `O desenho está sobrepondo o talhão ${sobreposto.nome || 'já cadastrado'}. Aproxime os pontos para usar a aderência automática.`,
    };
  }
  return { valido: true, codigo: 'valido', mensagem: '' };
}
